// Clients B2B — the fields of a client, rendered from ONE table
// (clientsB2B.js B2B_FIELDS / B2B_SECTIONS) in the three places they appear:
// the create modal (ClientsInstructions.js), the edit form and the read-only
// preview (ClientEDNDetail.js). A field added to the table shows up in all
// three; ClientB2BFields.test.js checks it.
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AtSign, Ban, Bot, BellRing, Building2, ChevronRight, ExternalLink, Plus, Sparkles, Truck, X,
} from 'lucide-react';
import { Badge, Card, Field, Switch } from '../design/ui';
import {
  B2B_FIELDS, B2B_SECTIONS, SOUFFRANCE_LANGUAGES, addListEntry, aliasProvenanceOf, unknownDisabledCarriers,
} from './clientsB2B';
import { formatDay } from './clientsShared';
import './ClientB2BFields.css';

const SECTION_ICONS = { identity: Building2, detection: AtSign, agent: Bot, automation: Truck, souffrance: BellRing };
const HALF_KINDS = ['text', 'url', 'email', 'language'];
// Anchors of the guided tour / copilot (Backend copilot_catalog.py).
const TOUR_ANCHORS = { emails: 'client-emails', automation: 'client-automation' };

export const fieldLabel = (t, key) => t(`clientsUi.fields.${key}.label`, B2B_FIELDS[key].label);
const fieldHelp = (t, key) => (B2B_FIELDS[key].help ? t(`clientsUi.fields.${key}.help`, B2B_FIELDS[key].help) : undefined);
const fieldPlaceholder = (t, key) => (B2B_FIELDS[key].placeholder ? t(`clientsUi.fields.${key}.placeholder`, B2B_FIELDS[key].placeholder) : undefined);
export const sectionTitle = (t, section) => t(`clientsUi.sections.${section.key}.title`, section.title);
const sectionDesc = (t, section) => t(`clientsUi.sections.${section.key}.desc`, section.desc);

export const languageLabel = (t, language) => t(`clientEDNDetail.languages.${language}`, language);

// « Par défaut (Anglais, vos réglages généraux) » or « Par défaut (français) ».
const defaultLanguageLabel = (t, generalLanguage) => (generalLanguage
  ? t('clientsUi.form.languageDefaultGeneral', 'Par défaut ({{language}}, vos réglages généraux)', { language: languageLabel(t, generalLanguage) })
  : t('clientsUi.form.languageDefault', 'Par défaut (français)'));

const provenanceText = (t, entry) => t(
  'clientEDNDetail.aliasProvenance',
  '« {{alias}} » ajouté par l\'IA le {{date}} : enseigne vérifiée sur {{carrier}} (colis {{tracking}} retrouvé sous cette enseigne).',
  {
    alias: entry.alias,
    date: formatDay(entry.at) || '—',
    carrier: entry.carrier || t('clientEDNDetail.aliasProvenanceCarrier', 'le portail transporteur'),
    tracking: entry.verified_tracking_number || '—',
  },
);

// A list of entries (aliases, e-mails): tags with a remove button + one input.
// The typed entry is added on « Ajouter », Enter or when the field loses focus,
// so a value typed and not added is never lost on save.
const ListEditor = ({ id, values, onChange, placeholder, addLabel, renderTag }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const commit = () => {
    const next = addListEntry(values, draft);
    if (next !== values) onChange(next);
    setDraft('');
  };
  return (
    <div className="clients-form__list">
      {values.length > 0 && (
        <ul className="clients-form__tags">
          {values.map((value, index) => (
            <li key={`${value}-${index}`} className="u-tag clients-form__tag">
              {renderTag ? renderTag(value) : (value || <span className="u-muted">{t('clientsUi.form.emptyEntry', '(vide)')}</span>)}
              <button
                type="button"
                className="clients-form__tag-remove"
                onClick={() => onChange(values.filter((_, i) => i !== index))}
                aria-label={t('clientsUi.form.removeEntry', 'Retirer {{value}}', { value })}
                title={t('clientsUi.form.removeEntry', 'Retirer {{value}}', { value })}
              >
                <X strokeWidth={2} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="u-inline-form">
        <input
          id={id}
          type="text"
          className="u-input"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
        />
        <button type="button" className="u-btn u-btn--secondary" onClick={commit} disabled={!draft.trim()}>
          <Plus aria-hidden="true" />
          {addLabel}
        </button>
      </div>
    </div>
  );
};

const AliasTag = ({ alias, client, t }) => {
  const entry = aliasProvenanceOf(client, alias);
  if (!entry) return alias;
  return (
    <span className="clients-form__ai-alias" title={provenanceText(t, entry)}>
      <Sparkles strokeWidth={1.8} aria-label={t('clientEDNDetail.aliasByAi', 'Ajouté par l\'IA')} />
      {alias}
    </span>
  );
};

// ---------------------------------------------------------------- the form

export const ClientB2BForm = ({
  form, setForm, carriers, client, layout = 'modal', idPrefix = 'b2b', generalLanguage, nameError,
}) => {
  const { t } = useTranslation();
  const hasAdvanced = Boolean(form.b2b_email || form.b2c_email);
  const [showAdvanced, setShowAdvanced] = useState(hasAdvanced);
  const set = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));
  const id = (key) => `${idPrefix}-${key.replace(/_/g, '-')}`;

  const carrierOptions = () => {
    const known = (carriers || []).map((carrier) => carrier.name);
    const stale = unknownDisabledCarriers({ disabled_carriers: form.disabled_carriers }, carriers);
    return [...known.map((name) => ({ name, stale: false })), ...stale.map((name) => ({ name, stale: true }))];
  };

  const renderControl = (key) => {
    const field = B2B_FIELDS[key];
    const value = form[key];
    switch (field.kind) {
      case 'list':
        return (
          <ListEditor
            id={id(key)}
            values={value}
            onChange={(next) => set(key, next)}
            placeholder={fieldPlaceholder(t, key)}
            addLabel={key === 'aliases' ? t('clientsUi.form.addAlias', 'Ajouter') : t('clientsUi.form.addEmail', 'Ajouter')}
            renderTag={key === 'aliases' ? (alias) => <AliasTag alias={alias} client={client} t={t} /> : undefined}
          />
        );
      case 'textarea':
        return (
          <textarea
            id={id(key)}
            className="u-textarea"
            rows={key === 'instructions' ? 6 : 3}
            value={value}
            placeholder={fieldPlaceholder(t, key)}
            onChange={(e) => set(key, e.target.value)}
          />
        );
      case 'switch':
        return (
          <Switch
            id={id(key)}
            checked={Boolean(value)}
            onChange={(checked) => set(key, checked)}
            label={t('clientsUi.form.automationSwitch', 'Automatisation active pour ce client')}
          />
        );
      case 'language':
        return (
          <select id={id(key)} className="u-select" value={value} onChange={(e) => set(key, e.target.value)}>
            <option value="">{defaultLanguageLabel(t, generalLanguage)}</option>
            {SOUFFRANCE_LANGUAGES.map((language) => (
              <option key={language} value={language}>{languageLabel(t, language)}</option>
            ))}
            {value && !SOUFFRANCE_LANGUAGES.includes(value) && <option value={value}>{value}</option>}
          </select>
        );
      case 'carriers': {
        const options = carrierOptions();
        if (!options.length) {
          return <p className="u-help">{t('clientsUi.form.noCarriers', 'Aucun transporteur configuré pour votre compte.')}</p>;
        }
        return (
          <div className="clients-form__carriers" role="group" aria-labelledby={`${id(key)}-label`}>
            {options.map(({ name, stale }) => {
              const excluded = value.includes(name);
              return (
                <label key={name} className={`clients-form__carrier${excluded ? ' is-excluded' : ''}`}>
                  <input
                    type="checkbox"
                    checked={excluded}
                    onChange={(e) => set(key, e.target.checked ? [...value, name] : value.filter((item) => item !== name))}
                  />
                  <Truck strokeWidth={1.8} aria-hidden="true" />
                  <span className="clients-form__carrier-name">{name}</span>
                  {stale && <Badge>{t('clientsUi.form.carrierGone', 'n’existe plus')}</Badge>}
                  {excluded && !stale && <Badge tone="warning">{t('clientsUi.form.carrierExcluded', 'Exclu')}</Badge>}
                </label>
              );
            })}
          </div>
        );
      }
      default:
        return (
          <input
            id={id(key)}
            type={field.kind === 'email' ? 'email' : field.kind === 'url' ? 'url' : 'text'}
            className="u-input"
            value={value}
            placeholder={fieldPlaceholder(t, key)}
            aria-required={field.required || undefined}
            aria-invalid={key === 'name' && nameError ? 'true' : undefined}
            onChange={(e) => set(key, e.target.value)}
          />
        );
    }
  };

  const renderField = (key) => {
    const field = B2B_FIELDS[key];
    const label = `${fieldLabel(t, key)}${field.required ? ' *' : ''}`;
    const full = !HALF_KINDS.includes(field.kind);
    return (
      <div
        key={key}
        className={`clients-form__field${full ? ' clients-form__field--full' : ''}`}
        data-tour={TOUR_ANCHORS[key]}
        data-field={key}
      >
        {field.kind === 'carriers' ? (
          <div className="u-field">
            <span className="u-label" id={`${id(key)}-label`}>{label}</span>
            {renderControl(key)}
            <p className="u-help">{fieldHelp(t, key)}</p>
          </div>
        ) : (
          <Field
            label={field.kind === 'switch' ? undefined : label}
            htmlFor={id(key)}
            help={fieldHelp(t, key)}
            error={key === 'name' ? nameError : undefined}
          >
            {field.kind === 'switch' && <span className="u-label">{label}</span>}
            {renderControl(key)}
          </Field>
        )}
      </div>
    );
  };

  const renderSectionBody = (section) => {
    const main = section.fields.filter((key) => !B2B_FIELDS[key].advanced);
    const advanced = section.fields.filter((key) => B2B_FIELDS[key].advanced);
    return (
      <div className="clients-form__grid">
        {main.map(renderField)}
        {advanced.length > 0 && (
          <div className="clients-form__field clients-form__field--full">
            <button
              type="button"
              className="clients-form__advanced"
              aria-expanded={showAdvanced}
              onClick={() => setShowAdvanced((open) => !open)}
            >
              <ChevronRight strokeWidth={2} aria-hidden="true" />
              {t('clientsUi.form.advancedRouting', 'Avancé : adresses distinctes pour le B2B et le B2C')}
            </button>
          </div>
        )}
        {advanced.length > 0 && showAdvanced && advanced.map(renderField)}
      </div>
    );
  };

  if (layout === 'cards') {
    return (
      <div className="clients-form clients-form--cards">
        {B2B_SECTIONS.map((section) => (
          <Card key={section.key} icon={SECTION_ICONS[section.key]} title={sectionTitle(t, section)} description={sectionDesc(t, section)}>
            {renderSectionBody(section)}
          </Card>
        ))}
      </div>
    );
  }
  return (
    <div className="clients-form">
      {B2B_SECTIONS.map((section) => (
        <section key={section.key} className="clients-form__section" aria-label={sectionTitle(t, section)}>
          <div className="clients-form__section-head">
            <h3 className="clients-form__section-title">{sectionTitle(t, section)}</h3>
            <p className="u-help">{sectionDesc(t, section)}</p>
          </div>
          {renderSectionBody(section)}
        </section>
      ))}
    </div>
  );
};

// ------------------------------------------------------------- the preview

const Empty = ({ children }) => <span className="u-muted">{children}</span>;

export const ClientB2BPreview = ({ client, carriers, generalLanguage, onOpenTicket }) => {
  const { t } = useTranslation();
  const routing = client.email_routing || {};
  const stale = unknownDisabledCarriers(client, carriers);

  const value = (key) => {
    switch (key) {
      case 'name':
        return client.name;
      case 'client_id':
        return client.client_id ? <span className="u-code">{client.client_id}</span> : <Empty>{t('clientsUi.preview.none', 'Non renseigné')}</Empty>;
      case 'reference':
        return client.reference || <Empty>{t('clientsUi.preview.none', 'Non renseigné')}</Empty>;
      case 'aliases': {
        const aliases = client.aliases || [];
        if (!aliases.length) return <Empty>{t('clientsUi.preview.noAlias', 'Aucun alias')}</Empty>;
        const provenance = (client.alias_provenance || []).filter((entry) => aliases.some((alias) => alias.trim().toLowerCase() === String(entry.alias).trim().toLowerCase()));
        return (
          <div className="clients-preview__stack">
            <span className="clients-preview__tags">
              {aliases.map((alias) => (
                <span key={alias} className={`u-tag${aliasProvenanceOf(client, alias) ? ' clients-preview__ai-tag' : ''}`}>
                  <AliasTag alias={alias} client={client} t={t} />
                </span>
              ))}
            </span>
            {provenance.map((entry) => (
              <p key={`prov-${entry.alias}`} className="clients-preview__provenance">
                <Sparkles strokeWidth={1.8} aria-hidden="true" />
                <span>
                  {provenanceText(t, entry)}
                  {entry.ticket_id && onOpenTicket && (
                    <>
                      {' '}
                      <button type="button" className="u-link clients-preview__inline-link" onClick={() => onOpenTicket(entry.ticket_id)}>
                        {t('clientEDNDetail.aliasProvenanceTicket', 'Voir le ticket')}
                      </button>
                    </>
                  )}
                </span>
              </p>
            ))}
          </div>
        );
      }
      case 'emails': {
        const emails = (client.emails || []).filter((entry) => String(entry || '').trim());
        if (!emails.length) {
          return <span className="clients-preview__warn">{t('clientsUi.preview.noEmail', 'Aucune : ses e-mails entrants ne lui sont pas rattachés automatiquement.')}</span>;
        }
        return <span className="clients-preview__tags">{emails.map((email) => <span key={email} className="u-tag">{email}</span>)}</span>;
      }
      case 'instructions':
        return client.instructions
          ? <p className="clients-preview__text">{client.instructions}</p>
          : <Empty>{t('clientsUi.preview.noInstructions', 'Aucune consigne propre à ce client : l’agent applique vos consignes générales.')}</Empty>;
      case 'effitrace_url':
        return client.effitrace_url ? (
          <a className="u-link clients-preview__url" href={client.effitrace_url} target="_blank" rel="noopener noreferrer">
            {client.effitrace_url}
            <ExternalLink strokeWidth={1.8} aria-hidden="true" />
          </a>
        ) : <Empty>{t('clientsUi.preview.none', 'Non renseigné')}</Empty>;
      case 'automation':
        return client.automation
          ? <Badge tone="success" dot>{t('clientsUi.automationOn', 'Active')}</Badge>
          : <Badge>{t('clientsUi.automationOff', 'Désactivée')}</Badge>;
      case 'disabled_carriers': {
        const excluded = client.disabled_carriers || [];
        if (!excluded.length) return <Empty>{t('clientsUi.preview.noExcluded', 'Aucun : tous vos transporteurs sont utilisables.')}</Empty>;
        return (
          <span className="clients-preview__tags">
            {excluded.map((name) => (
              <Badge key={name} tone="warning" icon={Ban} title={stale.includes(name) ? t('clientsUi.preview.carrierGone', 'Ce transporteur n’est plus dans vos transporteurs') : undefined}>
                {name}
                {stale.includes(name) ? ` · ${t('clientsUi.form.carrierGone', 'n’existe plus')}` : ''}
              </Badge>
            ))}
          </span>
        );
      }
      case 'default_email':
      case 'b2b_email':
      case 'b2c_email':
        return routing[key] || (
          <Empty>
            {key === 'default_email'
              ? t('clientsUi.preview.noRouting', 'Non renseignée : pas d’avis envoyé à une adresse propre')
              : t('clientsUi.preview.sameAsDefault', 'Comme l’adresse des avis')}
          </Empty>
        );
      case 'souffrance_language':
        return client.souffrance_language
          ? languageLabel(t, client.souffrance_language)
          : <Empty>{defaultLanguageLabel(t, generalLanguage)}</Empty>;
      case 'souffrance_instructions':
        return client.souffrance_instructions
          ? <p className="clients-preview__text">{client.souffrance_instructions}</p>
          : <Empty>{t('clientsUi.preview.noSouffranceInstructions', 'Aucune : vos consignes générales s’appliquent.')}</Empty>;
      default:
        return client[key] ?? '—';
    }
  };

  return (
    <div className="clients-preview">
      {B2B_SECTIONS.map((section) => (
        <Card key={section.key} icon={SECTION_ICONS[section.key]} title={sectionTitle(t, section)} description={sectionDesc(t, section)}>
          <dl className="clients-preview__dl">
            {section.fields.map((key) => (
              <div key={key} className="clients-preview__row" data-field={key}>
                <dt className="u-overline">{fieldLabel(t, key)}</dt>
                <dd>{value(key)}</dd>
              </div>
            ))}
          </dl>
        </Card>
      ))}
    </div>
  );
};
